<template>
  <div class="taskpane">
    <div class="header">
      <h2>Template Builder</h2>
    </div>

    <label class="field-label" for="source-type">Source type</label>
    <select
      id="source-type"
      v-model.number="sourceType"
      class="picker"
      :disabled="loading"
    >
      <option
        v-for="option in sourceTypeOptions"
        :key="option.value"
        :value="option.value"
      >
        {{ option.title }}
      </option>
    </select>

    <div v-if="error" class="error-banner">
      {{ error }}
      <button type="button" class="link-btn" @click="() => load(sourceType)">Retry</button>
    </div>

    <div v-else-if="loading" class="status">Loading tags…</div>

    <div v-else-if="isEmpty" class="info-banner">
      No tags supported yet for this source type. Its mapper is on the roadmap.
    </div>

    <TagPicker
      v-else-if="tags"
      :tags="tags"
      @insert-status="status = $event"
    />

    <div class="status" role="status" aria-live="polite">{{ status }}</div>

    <DocumentCheckPanel v-if="tags" :tags="tags" />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import TagPicker from './components/TagPicker.vue';
import DocumentCheckPanel from './components/DocumentCheckPanel.vue';
import { fetchSupportedTags } from '@/services/api';
import {
  DocumentTemplateSourceType,
  sourceTypeLabels,
  type SupportedTags,
} from '@/types/SupportedTags';

const sourceType = ref<DocumentTemplateSourceType>(DocumentTemplateSourceType.Contract);
const tags = ref<SupportedTags | null>(null);
const loading = ref(false);
const error = ref<string | null>(null);
const status = ref<string>('');

const sourceTypeOptions = [
  { value: DocumentTemplateSourceType.Contract, title: sourceTypeLabels[DocumentTemplateSourceType.Contract] },
  { value: DocumentTemplateSourceType.SpecHome, title: sourceTypeLabels[DocumentTemplateSourceType.SpecHome] },
  { value: DocumentTemplateSourceType.ChangeOrder, title: sourceTypeLabels[DocumentTemplateSourceType.ChangeOrder] },
];

const isEmpty = computed(
  () => !!tags.value && tags.value.fieldTags.length === 0 && tags.value.groups.length === 0,
);

async function load(type: DocumentTemplateSourceType): Promise<void> {
  loading.value = true;
  error.value = null;
  try {
    tags.value = await fetchSupportedTags(type);
  } catch (e) {
    error.value = e instanceof Error
      ? 'Unable to load tag list. Check your internet connection and try again.'
      : String(e);
    tags.value = null;
  } finally {
    loading.value = false;
  }
}

watch(sourceType, (v) => void load(v), { immediate: true });
</script>

<style scoped>
.header {
  margin-bottom: 4px;
}

.header h2 {
  font-size: 15px;
  margin: 0 0 4px;
}

.link-btn {
  border: none;
  background: transparent;
  color: #0078d4;
  font: inherit;
  cursor: pointer;
  padding: 2px 4px;
  margin-left: 8px;
  border-radius: 2px;
}

.link-btn:hover {
  background: #edebe9;
  text-decoration: underline;
}
</style>
